import { FormControl, MenuItem, TextField, Typography } from '@mui/material';
import { Control, Controller, RegisterOptions } from 'react-hook-form';

export interface SelectOptionGroup {
  groupName: string;
  options: any[];
}

interface GroupedSelectMenuProps {
  label: string;
  name: string;
  values: SelectOptionGroup[];
  valueAttr: string;
  labelAttr: string;
  selectedLabelAttr?: string;
  disabledAttr?: string;
  control: Control<any>;
  required?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  rules?: Exclude<
    RegisterOptions,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs'
  >;
  defaultValue?: any;
  className?: string;
  onChange?: (evt: any) => void;
}

export default function GroupedSelectMenu({
  label,
  name,
  control,
  values,
  valueAttr,
  labelAttr,
  selectedLabelAttr,
  disabledAttr,
  required = false,
  disabled = false,
  errorMessage,
  rules = undefined,
  defaultValue = undefined,
  className = undefined,
  onChange,
}: GroupedSelectMenuProps) {
  const getSelectedLabel = (value: any) => {
    const matchingValue = values
      .map((v) => v.options)
      .flat()
      .find((v) => v[valueAttr] === value);
    if (matchingValue) {
      return selectedLabelAttr
        ? matchingValue[selectedLabelAttr]
        : matchingValue[labelAttr];
    }
    return '';
  };

  return (
    <Controller
      name={name as any}
      control={control}
      defaultValue={defaultValue || ''}
      rules={rules}
      render={({ field }) => (
        <FormControl className={className}>
          <TextField
            {...field}
            select
            required={required}
            label={label}
            variant="outlined"
            disabled={disabled}
            helperText={errorMessage}
            error={!!errorMessage}
            onChange={onChange ? onChange : field.onChange}
            SelectProps={{
              renderValue: (value) => {
                return getSelectedLabel(value);
              },
            }}>
            {values.map((group, groupIndex) => [
              group.groupName && (
                <Typography
                  variant="h5"
                  key={`group-${groupIndex}`}
                  sx={{
                    margin: 0,
                    paddingX: (theme) => theme.spacing(2),
                    paddingY: (theme) => theme.spacing(1),
                    borderBottom: 1,
                    borderColor: (theme) => theme.palette.divider,
                  }}>
                  {group.groupName}
                </Typography>
              ),
              group.options.map((option, optionIndex) => (
                <MenuItem
                  key={`${groupIndex}-${optionIndex}`}
                  value={option[valueAttr]}
                  disabled={disabledAttr ? option[disabledAttr] : false}
                  sx={{
                    paddingLeft: (theme) => theme.spacing(3),
                    borderBottom: 1,
                    borderColor: (theme) => theme.palette.divider,
                  }}>
                  {option[labelAttr]}
                </MenuItem>
              )),
            ])}
          </TextField>
        </FormControl>
      )}
    />
  );
}
